import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, message, Space, Popconfirm, Tooltip, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
// 确保从正确的路径导入API，特别是新增的 getProducts
import { getLicenses, createLicense, updateLicense, deleteLicense, getProducts } from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const LicenseList = () => {
    const [licenses, setLicenses] = useState([]);
    const [products, setProducts] = useState([]); // 新增：存储产品列表用于下拉选择
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [searchForm] = Form.useForm(); // 使用 Form.useForm()
    const [modalForm] = Form.useForm(); // Modal 使用独立的 form 实例
    const [modalVisible, setModalVisible] = useState(false);
    const [editingLicense, setEditingLicense] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]); // 新增：用于存储选中的行ID


    // 获取产品列表（用于创建/编辑许可证时的下拉选择）
    const fetchProductsForSelect = async () => {
        try {
            // 通常我们获取所有产品用于选择，或者至少是激活状态的产品
            // 这里简化为获取前100个产品，实际项目中可能需要分页或更好的产品选择器
            const response = await getProducts({ page: 1, per_page: 100 });
            if (response.data && response.data.products) {
                setProducts(response.data.products);
            }
        } catch (error) {
            message.error('获取产品列表失败，无法创建/编辑许可证关联的产品');
            console.error("获取产品列表失败:", error);
        }
    };


    const fetchLicenses = async (page = pagination.current, pageSize = pagination.pageSize, params = {}) => {
        setLoading(true);
        try {
            const searchValues = await searchForm.getFieldsValue(); // 从form实例获取值
            const { created_at_range, ...restValues } = searchValues;

            // 处理 is_blocked 的值，确保空字符串或 undefined 不会发送给后端
            const queryParams = {
                page,
                per_page: pageSize,
                ...restValues, // 包含 key, notes, product_identifier, is_blocked
                created_at_start: created_at_range && created_at_range[0] ? created_at_range[0].format('YYYY-MM-DDTHH:mm:ssZ') : '',
                created_at_end: created_at_range && created_at_range[1] ? created_at_range[1].format('YYYY-MM-DDTHH:mm:ssZ') : '',
                ...params,
            };

            // 如果 is_blocked 是 undefined (用户没有选择 "全部" 以外的选项)，则不应作为查询参数发送
            // Antd Select 在 allowClear 时，清除后值为 undefined
            if (queryParams.is_blocked === undefined) {
                delete queryParams.is_blocked;
            }


            const response = await getLicenses(queryParams);
            setLicenses(response.data.licenses);
            setPagination({
                current: response.data.page || page, // 后端可能不返回 page 和 per_page
                pageSize: response.data.per_page || pageSize,
                total: response.data.total
            });
        } catch (error) {
            message.error('获取许可证列表失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLicenses();
        fetchProductsForSelect(); // 初始化时获取产品列表
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTableChange = (newPagination) => {
        fetchLicenses(newPagination.current, newPagination.pageSize);
    };

    const handleSearch = () => { // 修改：直接调用 fetchLicenses
        fetchLicenses(1, pagination.pageSize);
    };

    const resetSearch = () => {
        searchForm.resetFields();
        fetchLicenses(1, pagination.pageSize);
    };

    const showCreateModal = () => {
        setEditingLicense(null);
        modalForm.resetFields();
        // 默认可以不设置产品，让用户选择
        modalForm.setFieldsValue({ max_activations: 1, count: 1 }); // 默认最大激活次数为1, 创建数量为1
        setModalVisible(true);
    };

    const showEditModal = (license) => {
        setEditingLicense(license);
        modalForm.setFieldsValue({
            ...license,
            blocked_hours: license.blocked_hours || 0, // 如果license对象中有blocked_hours则填充，否则默认为0
            // product_identifier 字段应该直接从 license.product_identifier 获取
        });
        setModalVisible(true);
    };

    const handleModalOk = async () => {
        setModalLoading(true);
        try {
            const values = await modalForm.validateFields();
            const payload = { ...values };

            // 如果是编辑模式，且 blocked_hours 未填写或为0，可以考虑从 payload 中移除或设为 null
            // 这取决于 API 如何处理 0 或 null 的 blocked_hours
            if (editingLicense && (payload.blocked_hours === null || payload.blocked_hours === undefined)) {
                //  payload.blocked_hours = null; // 或者 delete payload.blocked_hours;
            }


            if (editingLicense) {
                // 编辑时不需要 count 字段
                delete payload.count;
                await updateLicense(editingLicense.id, payload);
                message.success('许可证更新成功');
            } else {
                // 创建时，count 字段由用户输入
                // payload中已经包含了 product_identifier, max_activations, notes, count
                // 创建时通常不设置 blocked_hours，如果需要，API应有默认处理
                delete payload.blocked_hours; // 创建时移除禁用时长
                await createLicense(payload);
                message.success('许可证创建成功');
            }
            setModalVisible(false);
            fetchLicenses(editingLicense ? pagination.current : 1, pagination.pageSize); // 创建则刷新到第一页
        } catch (errorInfo) {
            if (errorInfo.response && errorInfo.response.data && errorInfo.response.data.error) {
                message.error(`操作失败: ${errorInfo.response.data.error}`);
            } else if (errorInfo.errorFields) {
                message.error('请检查表单输入!');
            }
            else {
                message.error('操作失败，请重试');
            }
            console.error('Modal操作失败:', errorInfo);
        } finally {
            setModalLoading(false);
        }
    };

    const handleDelete = async (ids) => { // ids 参数现在是一个数组
        if (!ids || ids.length === 0) {
            message.warn('请选择要删除的许可证');
            return;
        }
        try {
            await deleteLicense({ license_ids: ids }); // API调用修改
            message.success(`成功删除 ${ids.length} 个许可证`);
            setSelectedRowKeys([]); // 清空选择
            // 计算删除后应该停留在的页码
            const totalAfterDelete = pagination.total - ids.length;
            const pageSize = pagination.pageSize;
            const currentTotalPages = Math.ceil(totalAfterDelete / pageSize);
            const newCurrentPage = Math.min(pagination.current, currentTotalPages);

            fetchLicenses(Math.max(1, newCurrentPage), pageSize); // 确保页码不小于1

        } catch (error) {
            message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a,b) => a.id - b.id },
        { title: '密钥', dataIndex: 'key', key: 'key' },
        {
            title: '产品名称', // 显示产品名称而非仅标识符，更友好
            dataIndex: 'product_name', // 假设 to_dict 返回 product_name
            key: 'product_name',
            render: (text, record) => text || record.product_identifier || 'N/A', // 优先显示名称
        },
        { title: '产品标识符', dataIndex: 'product_identifier', key: 'product_identifier' }, // 仍然保留标识符列
        { title: '最大激活', dataIndex: 'max_activations', key: 'max_activations', sorter: (a,b) => a.max_activations - b.max_activations },
        { title: '当前激活', dataIndex: 'current_activations', key: 'current_activations', sorter: (a,b) => a.current_activations - b.current_activations },
        { title: '剩余激活', dataIndex: 'remaining_activations', key: 'remaining_activations', sorter: (a,b) => a.remaining_activations - b.remaining_activations },
        {
            title: '是否禁用',
            dataIndex: 'blocked_until', // 使用 blocked_until 作为数据源
            key: 'is_blocked_status', // key 可以保持唯一性
            render: (blocked_until_text, record) => {
                if (blocked_until_text) {
                    const blockedUntilDate = new Date(blocked_until_text);
                    const now = new Date();
                    // 检查日期是否有效，以防 blocked_until_text 不是有效的日期字符串
                    if (!isNaN(blockedUntilDate.getTime()) && blockedUntilDate > now) {
                        return '是'; // 禁用
                    }
                }
                return '否'; // 可用
            },
            // 如果需要根据这个动态计算的状态排序，sorter会比较复杂，
            // 可能需要在获取数据时由后端直接提供一个 is_currently_blocked 字段或在前端预处理
        },
        { title: '禁用时间', dataIndex: 'blocked_until' },
        { title: '备注', dataIndex: 'notes', key: 'notes', ellipsis: true },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? new Date(text).toLocaleString() : '-',
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="编辑">
                        <Button icon={<EditOutlined />} onClick={() => showEditModal(record)} />
                    </Tooltip>
                    <Popconfirm
                        title={`确定删除密钥 "${record.key}" 吗?`}
                        onConfirm={() => handleDelete([record.id])} // 修改为传入数组
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;

    return (
        <>
            <Form form={searchForm} layout="inline" onFinish={handleSearch} style={{ marginBottom: 16 }}>
                <Form.Item name="key" label="密钥">
                    <Input placeholder="输入密钥" allowClear />
                </Form.Item>
                <Form.Item name="product_name" label="产品名称">
                    <Input placeholder="输入产品名称" allowClear />
                </Form.Item>
                <Form.Item name="product_identifier" label="产品标识符">
                    <Input placeholder="输入产品标识符" allowClear />
                </Form.Item>
                <Form.Item name="is_blocked" label="是否被禁用">
                    <Select placeholder="选择状态" allowClear style={{ width: 120 }}>
                        <Option value={false}>可用</Option>
                        <Option value={true}>禁用</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="notes" label="备注">
                    <Input placeholder="输入备注" allowClear />
                </Form.Item>
                <Form.Item name="created_at_range" label="创建时间">
                    <RangePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                            搜索
                        </Button>
                        <Button onClick={resetSearch}>
                            重置
                        </Button>
                    </Space>
                </Form.Item>
            </Form>

            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
                    创建许可证
                </Button>
                <Popconfirm
                    title={`确定删除选中的 ${selectedRowKeys.length} 个许可证吗?`}
                    onConfirm={() => handleDelete(selectedRowKeys)}
                    okText="确定"
                    cancelText="取消"
                    disabled={!hasSelected}
                >
                    <Button
                        type="danger"
                        icon={<DeleteOutlined />}
                        disabled={!hasSelected}
                        loading={loading && hasSelected} // 可选：在删除进行中时显示loading
                    >
                        批量删除
                    </Button>
                </Popconfirm>
                {hasSelected && <span style={{ marginLeft: 8 }}>已选择 {selectedRowKeys.length} 项</span>}
            </Space>
            <Table
                rowSelection={rowSelection} // 添加行选择配置
                columns={columns}
                dataSource={licenses}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }} // 保证横向滚动
            />
            <Modal
                title={editingLicense ? '编辑许可证' : '创建许可证'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                confirmLoading={modalLoading}
                destroyOnClose // 关闭时销毁表单内容
            >
                <Form form={modalForm} layout="vertical" name="licenseForm">
                    <Form.Item
                        name="product_identifier"
                        label="关联产品"
                        rules={[{ required: true, message: '请选择或输入产品标识符!' }]}
                    >
                        <Select
                            showSearch
                            placeholder="选择或输入产品标识符"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase()) ||
                                (option.value?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                            }
                            allowClear
                        >
                            {products.map(product => (
                                <Option key={product.identifier} value={product.identifier}>
                                    {product.name} ({product.identifier})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="max_activations"
                        label="最大激活次数"
                        rules={[{ required: true, message: '请输入最大激活次数!' }]}
                        initialValue={1}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    {editingLicense && ( // 仅在编辑时显示禁用时长
                        <Form.Item
                            name="blocked_hours"
                            label="禁用时长 (小时)"
                            rules={[
                                {
                                    type: 'integer',
                                    message: '请输入一个整数!',
                                },
                                {
                                    validator: (_, value) => {
                                        if (value === null || value === undefined || value === '' || Number.isInteger(Number(value)) && Number(value) >= 0) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('禁用时长必须是大于等于0的整数'));
                                    }
                                },
                            ]}
                            initialValue={0}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} placeholder="输入小时数，0或空表示不禁用" />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="notes"
                        label="备注"
                        rules={[{ max: 255, message: "备注不能超过255字符!"}]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    {!editingLicense && ( // 仅在创建时显示创建数量
                        <Form.Item
                            name="count"
                            label="创建数量"
                            initialValue={1}
                            rules={[{ required: true, message: '请输入创建数量!' }]}
                        >
                            <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default LicenseList;
